import api from "../utils/api";
import type {
  AccessProfileDoc,
  AccessProfileUsage,
  AccessProfileUsageEntry,
  CompanyAccessDoc,
  RoleAccessDoc,
  UserAccessDoc,
} from "../config/access/types";

export interface AccessLayers {
  role: RoleAccessDoc | null;
  user: UserAccessDoc | null;
  company: CompanyAccessDoc | null;
  enforced: boolean;
  allowed: string[];
  /** true para el owner: el API se salta todas las capas (CC-CONSOLE #1024). */
  bypass?: boolean;
  /**
   * Perfiles vivos que alimentan cada capa (informativo). `role`/`user`/`company`
   * ya llegan PLANOS: el API los expande con el perfil actual.
   */
  profiles?: { role: string[]; user: string[]; company: string[] };
}

/** Código del 409 al intentar borrar un perfil asignado. */
export const PROFILE_IN_USE_CODE = "ACCESS_PROFILE_IN_USE";

/** Error del borrado de un perfil que alguien tiene asignado. */
export class ProfileInUseError extends Error {
  readonly usage: AccessProfileUsage;

  constructor(message: string, usage: AccessProfileUsage) {
    super(message);
    this.name = "ProfileInUseError";
    this.usage = usage;
  }
}

const EMPTY_LAYERS: AccessLayers = {
  role: null,
  user: null,
  company: null,
  enforced: false,
  allowed: [],
};

export interface SaveResultMeta {
  restricts: boolean;
}

export type SaveRoleResult = RoleAccessDoc & SaveResultMeta;

export type SaveUserResult = UserAccessDoc & SaveResultMeta;

export type SaveCompanyResult = CompanyAccessDoc & SaveResultMeta;

export type SaveProfileResult = AccessProfileDoc & SaveResultMeta;

export const accessService = {
  async me(): Promise<AccessLayers> {
    try {
      const { data } = await api.get("/access/me");
      return normalizeLayers(data?.data);
    } catch (err) {
      if (isMissing(err)) return EMPTY_LAYERS;
      throw err;
    }
  },

  async getRole(roleId: string): Promise<RoleAccessDoc | null> {
    try {
      const { data } = await api.get("/access/role", { params: { id: roleId } });
      return normalizeRole(data?.data);
    } catch (err) {
      if (isMissing(err)) return null;
      throw err;
    }
  },

  async saveRole(payload: RoleAccessDoc): Promise<SaveRoleResult> {
    const { data } = await api.put("/access/role", payload);
    return { ...(normalizeRole(data?.data) ?? payload), ...saveMeta(data?.data) };
  },

  async getCompany(companyId: string): Promise<CompanyAccessDoc | null> {
    try {
      const { data } = await api.get("/access/company", {
        params: { id: companyId },
      });
      return normalizeCompany(data?.data);
    } catch (err) {
      if (isMissing(err)) return null;
      throw err;
    }
  },

  async saveCompany(payload: CompanyAccessDoc): Promise<SaveCompanyResult> {
    const { data } = await api.put("/access/company", payload);
    return {
      ...(normalizeCompany(data?.data) ?? payload),
      ...saveMeta(data?.data),
    };
  },

  async listProfiles(): Promise<AccessProfileDoc[]> {
    try {
      const { data } = await api.get("/access/profiles");
      const rows = Array.isArray(data?.data) ? data.data : [];
      return rows.map(normalizeProfile);
    } catch (err) {
      if (isMissing(err)) return [];
      throw err;
    }
  },

  async saveProfile(payload: AccessProfileDoc): Promise<SaveProfileResult> {
    const { data } = await api.put("/access/profile", payload);
    return { ...normalizeProfile(data?.data), ...saveMeta(data?.data) };
  },

  async getProfileUsage(profileId: string): Promise<AccessProfileUsage> {
    const { data } = await api.get("/access/profile/usage", {
      params: { id: profileId },
    });
    return normalizeUsage(data?.data, profileId);
  },

  /** Lanza `ProfileInUseError` si el perfil está asignado (409). */
  async deleteProfile(profileId: string): Promise<void> {
    try {
      await api.delete("/access/profile", { params: { id: profileId } });
    } catch (err) {
      const response = (err as {
        response?: { status?: number; data?: Record<string, unknown> };
      })?.response;

      if (response?.status === 409 && response.data?.code === PROFILE_IN_USE_CODE) {
        throw new ProfileInUseError(
          typeof response.data.error === "string" ? response.data.error : "",
          normalizeUsage(response.data.data, profileId),
        );
      }
      throw err;
    }
  },

  async getUser(userId: string): Promise<UserAccessDoc | null> {
    try {
      const { data } = await api.get("/access/user", { params: { id: userId } });
      return normalizeUser(data?.data);
    } catch (err) {
      if (isMissing(err)) return null;
      throw err;
    }
  },

  async saveUser(payload: UserAccessDoc): Promise<SaveUserResult> {
    const { data } = await api.put("/access/user", payload);
    return { ...(normalizeUser(data?.data) ?? payload), ...saveMeta(data?.data) };
  },
};

function saveMeta(raw: unknown): SaveResultMeta {
  const source = (raw ?? {}) as Record<string, unknown>;
  return {
    restricts: source.restricts === true,
  };
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function normalizeRole(raw: unknown): RoleAccessDoc | null {
  if (raw === null || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;
  if (typeof source.role_id !== "string") return null;

  return {
    role_id: source.role_id,
    allow: asStringArray(source.allow),
    profile_ids: asStringArray(source.profile_ids),
    deny: asStringArray(source.deny),
  };
}

function normalizeUser(raw: unknown): UserAccessDoc | null {
  if (raw === null || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;
  if (typeof source.user_id !== "string") return null;

  return {
    user_id: source.user_id,
    mode: source.mode === "replace" ? "replace" : "inherit",
    profile_ids: asStringArray(source.profile_ids),
    grant: asStringArray(source.grant),
    deny: asStringArray(source.deny),
  };
}

function normalizeProfile(raw: unknown): AccessProfileDoc {
  const source = (raw ?? {}) as Record<string, unknown>;

  return {
    id: typeof source.id === "string" ? source.id : null,
    name: typeof source.name === "string" ? source.name : "",
    description: typeof source.description === "string" ? source.description : "",
    allow: asStringArray(source.allow),
  };
}

function normalizeCompany(raw: unknown): CompanyAccessDoc | null {
  if (raw === null || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;

  return {
    company_id: typeof source.company_id === "string" ? source.company_id : "",
    allow: asStringArray(source.allow),
    profile_ids: asStringArray(source.profile_ids),
    deny: asStringArray(source.deny),
  };
}

function normalizeUsageEntries(value: unknown): AccessProfileUsageEntry[] {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is Record<string, unknown> => item !== null && typeof item === "object")
    .map((item) => ({
      id: typeof item.id === "string" ? item.id : "",
      name: typeof item.name === "string" ? item.name : "",
      ...(typeof item.email === "string" ? { email: item.email } : {}),
    }));
}

function normalizeUsage(raw: unknown, profileId: string): AccessProfileUsage {
  const source = (raw ?? {}) as Record<string, unknown>;
  const users = normalizeUsageEntries(source.users);
  const roles = normalizeUsageEntries(source.roles);
  const companies = normalizeUsageEntries(source.companies);

  return {
    id: typeof source.id === "string" ? source.id : profileId,
    users,
    roles,
    companies,
    total:
      typeof source.total === "number"
        ? source.total
        : users.length + roles.length + companies.length,
  };
}

function normalizeLayers(raw: unknown): AccessLayers {
  if (raw === null || typeof raw !== "object") return EMPTY_LAYERS;
  const source = raw as Record<string, unknown>;

  const role = normalizeRole(source.role);
  const user = normalizeUser(source.user);
  const company = normalizeCompany(source.company);

  return {
    role,
    user,
    company,
    enforced:
      typeof source.enforced === "boolean"
        ? source.enforced
        : role !== null || user !== null || company !== null,
    allowed: asStringArray(source.allowed),
    bypass: source.bypass === true,
    profiles: normalizeProfileRefs(source.profiles),
  };
}

function normalizeProfileRefs(raw: unknown): NonNullable<AccessLayers["profiles"]> {
  const source = (raw ?? {}) as Record<string, unknown>;

  return {
    role: asStringArray(source.role),
    user: asStringArray(source.user),
    company: asStringArray(source.company),
  };
}

function isMissing(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 501;
}

/** Misma normalización que `accessService.me()`, para los accesos que ya vienen en /login. */
export { normalizeLayers as normalizeAccessLayers };

export default accessService;
